"use client";

import {
  type TimelineCollapsedSectionsState,
  useTimelineCollapsedSections,
  useTimelineSectionToggle,
} from "./useTimelineCollapsedSections";
import {
  useTimelineNeedleController,
  type TimelineNeedleController,
} from "./useTimelineNeedleController";
import { useTimelineParsedReset } from "./useTimelineParsedReset";
import {
  useTimelineLayoutMetrics,
  useTimelineRootMetrics,
  type TimelineRootMetrics,
} from "./useTimelineRootMetrics";
import {
  useTimelineRootSnapModel,
  useTimelineSnapCandidates,
  type TimelineRootSnapModel,
} from "./useTimelineRootSnapModel";
import {
  useTimelineScrollController,
  type TimelineScrollController,
} from "./useTimelineScrollController";
import {
  useTimelineTooltipController,
  type TimelineTooltipController,
} from "./useTimelineTooltipController";
import type { ParsedTimeline } from "./timeline-types";

export interface UseTimelineRootModelInput {
  parsed: ParsedTimeline | null;
}

export interface TimelineRootModel {
  collapsedSections: Set<number>;
  handleToggleSection: (sectionIndex: number) => void;
  metrics: TimelineRootMetrics;
  needleController: TimelineNeedleController;
  scrollController: TimelineScrollController;
  snapModel: TimelineRootSnapModel;
  tooltipController: TimelineTooltipController;
}

interface UseTimelineRootInteractionModelInput {
  collapsedSections: Set<number>;
  metrics: TimelineRootMetrics;
  parsed: ParsedTimeline | null;
  scrollController: TimelineScrollController;
  setCollapsedSections: TimelineCollapsedSectionsState["setCollapsedSections"];
}

interface TimelineRootInteractionModel {
  handleToggleSection: (sectionIndex: number) => void;
  needleController: TimelineNeedleController;
  snapModel: TimelineRootSnapModel;
  tooltipController: TimelineTooltipController;
}

function useTimelineRootInteractionModel({
  collapsedSections,
  metrics,
  parsed,
  scrollController,
  setCollapsedSections,
}: UseTimelineRootInteractionModelInput): TimelineRootInteractionModel {
  const tooltipController = useTimelineTooltipController({
    scrollContainerRef: scrollController.scrollContainerRef,
    viewportState: scrollController.viewportState,
  });
  const snapCandidates = useTimelineSnapCandidates({
    collapsedSections,
    parsed,
  });
  const needleController = useTimelineNeedleController({
    clearTimelineTooltip: tooltipController.clearTimelineTooltip,
    getInnerXFromClient: scrollController.getInnerXFromClient,
    getViewportCenterInInner: scrollController.getViewportCenterInInner,
    layout: metrics.layout,
    parsed,
    snapCandidates,
    viewportScrollLeft: scrollController.viewportState.scrollLeft,
  });
  const snapModel = useTimelineRootSnapModel({
    snapCandidates,
    snappedPeriodIndex: needleController.snappedPeriodIndex,
  });
  const handleToggleSection = useTimelineSectionToggle({
    clearTimelineTooltip: tooltipController.clearTimelineTooltip,
    setCollapsedSections,
  });

  useTimelineParsedReset({
    clearTimelineTooltip: tooltipController.clearTimelineTooltip,
    parsed,
    resetTimelineNeedle: needleController.resetTimelineNeedle,
    setCollapsedSections,
  });

  return {
    handleToggleSection,
    needleController,
    snapModel,
    tooltipController,
  };
}

export function useTimelineRootModel({
  parsed,
}: UseTimelineRootModelInput): TimelineRootModel {
  const { collapsedSections, setCollapsedSections } =
    useTimelineCollapsedSections();
  const layout = useTimelineLayoutMetrics({
    collapsedSections,
    parsed,
  });
  const periodCount = parsed?.periods.length ?? 0;
  const scrollController = useTimelineScrollController({
    layoutContentWidth: layout?.contentWidth,
    periodCount,
  });
  const viewportWidth = scrollController.viewportState.width;
  const rootMetrics = useTimelineRootMetrics({
    layout,
    parsed,
    viewportWidth,
  });
  const interactionModel = useTimelineRootInteractionModel({
    collapsedSections,
    metrics: rootMetrics,
    parsed,
    scrollController,
    setCollapsedSections,
  });

  return {
    collapsedSections,
    handleToggleSection: interactionModel.handleToggleSection,
    metrics: rootMetrics,
    needleController: interactionModel.needleController,
    scrollController,
    snapModel: interactionModel.snapModel,
    tooltipController: interactionModel.tooltipController,
  };
}
